const MONTH_MAP: Record<string, string> = {
	jan: 'January',
	feb: 'February',
	mar: 'March',
	apr: 'April',
	may: 'May',
	jun: 'June',
	jul: 'July',
	aug: 'August',
	sept: 'September',
	oct: 'October',
	nov: 'November',
	dec: 'December',
};

interface SeasonRange {
	start: string;
	end: string;
	isContinuous: boolean;
	formattedRange: string;
}

const MONTH_ORDER = [
	'january',
	'february',
	'march',
	'april',
	'may',
	'june',
	'july',
	'august',
	'september',
	'october',
	'november',
	'december',
];

const formatMonth = (month: string): string => {
	const key = month.toLowerCase().trim();
	return MONTH_MAP[key] || month;
};

const getMonthIndex = (month: string): number => {
	const fullMonth = formatMonth(month).toLowerCase();
	return MONTH_ORDER.indexOf(fullMonth);
};

/**
 * Groups consecutive months together.
 * @param months - Array of abbreviated month strings
 * @returns Array of month groups, where each group is an array of consecutive months
 */
const groupConsecutiveMonths = (months: string[]): string[][] => {
	if (months.length === 0) return [];

	const monthIndices = months.map(getMonthIndex);
	const groups: string[][] = [];
	let currentGroup: string[] = [months[0]];

	for (let i = 1; i < months.length; i++) {
		const prevIndex = monthIndices[i - 1];
		const currentIndex = monthIndices[i];

		// Check if current month is consecutive to the previous one (including wrap-around)
		if (currentIndex === (prevIndex + 1) % 12) {
			currentGroup.push(months[i]);
		} else {
			groups.push(currentGroup);
			currentGroup = [months[i]];
		}
	}

	groups.push(currentGroup);

	// Check if first and last groups wrap around the year
	if (groups.length > 1) {
		const firstGroupFirstIndex = monthIndices[0];
		const lastGroupLastIndex = monthIndices[monthIndices.length - 1];

		// If last month + 1 wraps to first month (e.g., December -> January)
		if ((lastGroupLastIndex + 1) % 12 === firstGroupFirstIndex) {
			// Merge last and first groups
			const mergedGroup = [...groups[groups.length - 1], ...groups[0]];
			return [mergedGroup, ...groups.slice(1, -1)];
		}
	}

	return groups;
};

/**
 * Formats a group of months into a readable string.
 * @param group - Array of consecutive month strings
 * @returns Formatted string (e.g., "March to May" or "September")
 */
const formatMonthGroup = (group: string[]): string => {
	if (group.length === 1) {
		return formatMonth(group[0]);
	}
	return `${formatMonth(group[0])} to ${formatMonth(group[group.length - 1])}`;
};

/**
 * Checks if the provided months form a continuous range.
 * @param months - Array of abbreviated month strings
 * @returns true if months are continuous, false otherwise
 */
const areMonthsContinuous = (months: string[]): boolean => {
	if (months.length <= 1) return true;

	const monthIndices = months.map(getMonthIndex);

	// Check if all indices are valid
	if (monthIndices.some((idx) => idx === -1)) return false;

	// Check for continuous sequence
	for (let i = 0; i < monthIndices.length - 1; i++) {
		const current = monthIndices[i];
		const next = monthIndices[i + 1];
		// Check if next month is consecutive (considering wrap-around)
		if (next !== (current + 1) % 12) {
			return false;
		}
	}

	return true;
};

/**
 * Extracts the first and last months from an array and formats them to full names.
 * @param months - Array of abbreviated month strings (e.g., ['Nov', 'Dec', 'Jan'])
 * @returns An object containing the start and end full month names, whether they are continuous, and a formatted range string, or null if input is empty.
 */
export const getSeasonRangeLabels = (months: string[]): SeasonRange | null => {
	if (!months || months.length === 0) return null;

	const isContinuous = areMonthsContinuous(months);
	const groups = groupConsecutiveMonths(months);
	const formattedGroups = groups.map(formatMonthGroup);

	// Format the groups with proper punctuation
	let formattedRange: string;
	if (formattedGroups.length === 1) {
		formattedRange = formattedGroups[0];
	} else if (formattedGroups.length === 2) {
		formattedRange = `${formattedGroups[0]} and ${formattedGroups[1]}`;
	} else {
		// 3 or more groups: use commas and "and" before the last item
		const allButLast = formattedGroups.slice(0, -1).join(', ');
		const last = formattedGroups[formattedGroups.length - 1];
		formattedRange = `${allButLast} and ${last}`;
	}

	return {
		start: formatMonth(months[0]),
		end: formatMonth(months[months.length - 1]),
		isContinuous,
		formattedRange,
	};
};
